import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ScrollableContent } from 'src/components/ScrollableContent';
import { IActivity } from 'src/models/Activity';
import { toast } from 'react-toastify';
import {
  fetchActivities,
  getActivities,
} from 'src/redux/features/session/slice';
import {
  assignActivityToProject,
  fetchProjectsById,
} from 'src/redux/features/projects/projectsSlice';
import { getUserRoles } from 'src/redux/features/users/usersSlice';
import { CanView } from 'src/components/CanView';
import { isAuthorised } from 'src/utils/helpers';
import AntDButton from 'src/components/Button';
import en from 'src/messages/en.json';
import { Flex, Input, Switch, Table } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { setError } from 'src/redux/features/common/commonSlice';

const GlobalActivities = ({ active }) => {
  const dispatch = useDispatch();

  const columns = ['Name', 'Capitalizable', 'Status', ''];
  const { data: activities, loading: activitiesLoading } =
    useSelector(getActivities);

  const userRoles = useSelector(getUserRoles);

  const [data, setData] = useState<IActivity[]>(
    activities.filter((act) => act.isDefault),
  );
  const [isSaving, setIsSaving] = useState(false);

  const addRow = () => {
    // const hasAllActivities = activities.every((item) =>
    //   data.some((other) => other.id === item.id),
    // );

    // if (hasAllActivities) {
    //   toast.error('All the activites are added to this project.');

    //   return;
    // }

    const usedActivities = data.map((it) => it.name);
    const unUsedActivities = activities.filter((activity) => {
      return usedActivities.indexOf(activity.name) === -1;
    });

    const newRow: IActivity = {
      id: data.length + 1,
      name: unUsedActivities[0]?.name || '',
      isActive: true,
      isNew: true,
      isDefault: true,
      isCapitalizable: true,
    };
    const updatedDate = [...data];

    updatedDate.unshift(newRow);
    setData(updatedDate);
  };

  const removeRow = (index) => {
    const newData = [...data];

    newData.splice(index, 1);
    setData(newData);
  };

  const onAvtivityTextChange = (e, index) => {
    const activityName = e.target.value;
    const newData = [...data];

    newData[index] = {
      ...newData[index],
      name: activityName,
    };

    setData([...newData]);
  };

  const onStatusChange = (index) => {
    const clonedData = data.map((it) => {
      return { ...it };
    });
    const activeCount = clonedData.filter((x) => x.isActive).length;

    if (clonedData[index].isActive && activeCount === 1) {
      dispatch(setError(en.Activities.Error.AtleastOneActivity));

      return;
    }

    clonedData[index].isActive = !clonedData[index].isActive;
    setData([...clonedData]);
  };

  const onCapitalizableChange = (index) => {
    const clonedData = data.map((it) => {
      return { ...it };
    });

    clonedData[index].isCapitalizable = !clonedData[index].isCapitalizable;
    setData([...clonedData]);
  };

  const getActivitiesByProjectId = async (id: number) => {
    await dispatch(fetchProjectsById({ id, skipError: true }));
  };

  function identifyDuplicateNames(data) {
    const nameSet = new Set();
    const duplicates = [];

    for (const item of data) {
      const name = item.name;

      if (nameSet.has(name)) {
        duplicates.push(name);
      } else {
        nameSet.add(name);
      }
    }

    return duplicates;
  }

  const onSave = async () => {
    const isActivitiesValid = data.every((item) => item.name !== '');

    if (!isActivitiesValid) {
      dispatch(setError(en.Activities.Error.enterActivityName));

      return;
    }
    const duplicateActivities = identifyDuplicateNames(data);

    if (duplicateActivities && duplicateActivities.length > 0) {
      dispatch(setError(en.Activities.Error.activityNameUnique));

      return;
    }

    setIsSaving(true);

    const response = await dispatch(
      assignActivityToProject({
        id: 0,
        payload: {
          activities: data.map((it) => {
            return {
              ...it,
              isDefault: true,
              id: it.isNew ? 0 : it?.id,
            };
          }),
        },
      }),
    );

    setIsSaving(false);
    if (response.error) {
      return;
    }

    toast.success(en.Activities.Success.activitySaved);
    await dispatch(fetchActivities());
  };

  const hasAdminAccess = isAuthorised(userRoles.data, ['CapexAdmin']);

  const Columns = (isAuthorised: boolean) => [
    {
      title: en.Activities.Title.activityName,
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any, index: number) => (
        <Input
          placeholder={en.Activities.Placeholder.enterActivityName}
          value={text}
          disabled={!isAuthorised}
          onChange={(e) => onAvtivityTextChange(e, index)}
        />
      ),
      width: '40%',
    },
    {
      title: en.Activities.Title.capitalizable,
      dataIndex: 'isCapitalizable',
      key: 'isCapitalizable',
      render: (_: boolean, record: any, index: number) => (
        <Switch
          checked={record.isCapitalizable}
          disabled={!isAuthorised}
          onChange={() => onCapitalizableChange(index)}
          size="small"
        />
      ),
    },
    {
      title: en.Common.Title.active,
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_: boolean, record: any, index: number) => (
        <Switch
          checked={record.isActive}
          disabled={!isAuthorised}
          onChange={() => onStatusChange(index)}
          size="small"
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_: any, record: any, index: number) =>
        record.isNew ? (
          <AntDButton
            type="link"
            prefixIcon={<DeleteOutlined />}
            onClick={() => removeRow(index)}
            htmlType="button"
          />
        ) : (
          <></>
        ),
    },
  ];

  useEffect(() => {
    if (active === 'global') dispatch(fetchActivities());
  }, [dispatch, active]);
  useEffect(() => {
    setData(activities.filter((act) => act.isDefault));
  }, [activities]);

  return (
    <>
      <Flex justify="flex-end" style={{ paddingBottom: '1rem' }}>
        {!activitiesLoading && (
          <CanView userRole={userRoles.data} allowedRoles={['CapexAdmin']}>
            <AntDButton
              type="primary"
              onClick={addRow}
              statusOnComplete="Add Activity"
              htmlType="button"
            />
          </CanView>
        )}
      </Flex>

      <ScrollableContent
        minHeight={
          hasAdminAccess ? 'calc(100vh - 18.75rem)' : 'calc(100vh - 12.75rem)'
        }
        maxHeight={
          hasAdminAccess ? 'calc(100vh - 18.75rem)' : 'calc(100vh - 12.75rem)'
        }
      >
        <Table
          columns={Columns(isAuthorised(userRoles.data, ['CapexAdmin']))}
          dataSource={data}
          rowKey={(record) => record.id}
          pagination={false}
          sticky
        />
      </ScrollableContent>
      <Flex justify="flex-end" style={{ paddingTop: '1rem' }}>
        <CanView userRole={userRoles.data} allowedRoles={['CapexAdmin']}>
          <AntDButton
            onClick={onSave}
            type="primary"
            htmlType="button"
            loading={isSaving}
            status="Saving..."
            statusOnComplete="Save"
          />
        </CanView>
      </Flex>
    </>
  );
};

export default GlobalActivities;
